// Orchestration 层：调度数据准备 → PDF 渲染
// 职责：调用数据构建函数 → 条件渲染决策 → 拼装 PDF 内容

import { AnalysisItem, LegendItem, LocationData } from "helpers/models";
import { Content } from "pdfmake/interfaces";
import gap from "report/common/gap";
import page from "report/common/page";
import getTitle from "report/common/title";
import getTpye from "report/common/type";
import getFathom from "./detail/fathom";
import getOther from "./detail/other";
import getExtremeLayer from "./extreme/layer";
import getExtremeTable from "./extreme/table";
import getOverview from "./overview/overview";
import { buildExtremeTableData } from "./build-extreme-table";
import { buildGroupedData } from "./build-grouped-data";

interface LineSeriesPoint {
  value?: number;
  intensity?: number;
}

interface LineSeries {
  name?: string;
  data?: Array<LineSeriesPoint | number | null>;
  [key: string]: any;
}

/**
 * 生成线性分析风险页面内容
 *
 * 输入数据 → [数据准备层] → 纯数据结构 → [渲染层] → Content[]
 */
const get = async (
  t: any,
  locationSet: LocationData,
  analysisItems: AnalysisItem[],
  legendItems: LegendItem[],
  count: number,
  aggregationUnits: string[],
  unit: string,
  currency: string,
  lineLength: string,
  lineSeries?: LineSeries[],
  extremeMap?: Map<string, any>
): Promise<Content[]> => {
  // ========== 第一步：数据准备 ==========
  // 调用独立的分组数据构建函数
  const { grouped, fathom, legend, layerTitle, distanceUnit } =
    buildGroupedData(
      analysisItems,
      legendItems,
      locationSet.items || [],
      aggregationUnits
    );

  // 调用独立的极值计算函数
  const extremeTableData = buildExtremeTableData(
    t,
    analysisItems,
    legendItems,
    distanceUnit,
    extremeMap
  );

  // ========== 第二步：条件渲染决定 ==========
  const hasGroupDetailFathom = Object.keys(fathom).length > 0;
  const hasGroupDetail = Object.keys(grouped).length > 0;
  const hasExtremeTables = extremeTableData.length > 0;

  // ========== 第三步：组装 PDF 内容 ==========
  const extremeTablesContent: Content[] = extremeTableData.flatMap(
    (item, index) => [
      {
        stack: [
          getExtremeLayer(t, item.layerId, 10, 1),
          gap(10),
          getExtremeTable(t, item)
        ],
        unbreakable: true
      },
      ...(index < extremeTableData.length - 1 ? [gap(24)] : [])
    ]
  );

  // 组装最终返回数组
  const hasLineSeries = !!lineSeries?.length;

  const content: Content[] = [
    getTpye(t("report.hazard"), 802),
    gap(30),
    await getOverview(
      t,
      analysisItems,
      legendItems,
      aggregationUnits,
      lineSeries
    ),
    page()
  ];

  // 分析页
  if (hasGroupDetailFathom || hasGroupDetail) {
    // content.push(page());
    content.push(getTitle(t("report.analysis")));
    content.push(gap(10));
  }

  if (hasGroupDetailFathom) {
    content.push(
      getFathom(
        t,
        fathom,
        aggregationUnits,
        unit,
        currency,
        layerTitle,
        distanceUnit,
        lineLength
      )
    );
  }

  if (hasGroupDetail) {
    content.push(
      getOther(
        t,
        grouped,
        aggregationUnits,
        unit,
        currency,
        layerTitle,
        distanceUnit,
        lineLength
      )
    );
  }

  // 极值页
  if (hasExtremeTables) {
    // content.push(page());
    content.push(getTitle(t("report.analysis")));
    content.push(gap(10));
    content.push(...extremeTablesContent);
  }

  return content;
};

export default get;
